<script lang="ts">
    import InstagramIcon from "@apps/instagram/components/icons/InstagramIcon.svelte";
    import InstagramPostSeriesIcon from "@apps/instagram/components/icons/InstagramPostSeriesIcon.svelte";
    import InstagramPostVideoIcon from "@apps/instagram/components/icons/InstagramPostVideoIcon.svelte";
    import {
        INSTAGRAM_POST_MEDIA_VIDEO_TYPE,
        INSTAGRAM_POST_TAG_PRODUCT_TYPE,
    } from "@apps/instagram/constants";
    import type InstagramPostEntity from "@apps/instagram/entities/InstagramPostEntity";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import Observable from "@ui-utils/Observable.svelte";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import EmptyElement from "@components/EmptyElement.svelte";
    import Icon from "@components/Icon.svelte";
    import Image from "@components/Image/Image.svelte";
    import { ModalToken } from "@components/Modal/ModalManager";
    import { modalManagerContextKey } from "@components/Modal/ModalManager/constants";
    import Money from "@components/Money.svelte";
    import T from "@components/T.svelte";
    import TooltipActivator from "@components/Tooltip/TooltipActivator.svelte";

    import {
        DISPLAY_MODE_HOTSPOT_TYPE,
        DISPLAY_MODE_SHOP_IT_TYPE,
    } from "../constants";
    import { instagramGalleryWidgetContextToken } from "../context_tokens";

    export let post: InstagramPostEntity;
    export let square = false;
    export let postModalToken: ModalToken<{ id: number }>;
    export let imageRatio: number = 1 / 1;
    export let imageObjectFit: "cover" | "contain" | undefined = undefined;
    export let displayMode:
        | typeof DISPLAY_MODE_SHOP_IT_TYPE
        | typeof DISPLAY_MODE_HOTSPOT_TYPE
        | null;
    export let highQualityEnabled: boolean;
    const modalManager = getTypedContext(modalManagerContextKey);
    const { productsQuery } = getTypedContext(
        instagramGalleryWidgetContextToken
    );
    const handleOpen = () => {
        modalManager.openModal(postModalToken, undefined, {
            id: post.id,
        });
    };
</script>

<!-- svelte-ignore a11y-no-noninteractive-element-to-interactive-role -->
<div
    role="link"
    class="gw-ig-post-card"
    on:click={handleOpen}
    on:keypress={handleOpen}
    tabindex="0"
    class:gw-ig-post-card--square={square}
>
    <div class="gw-ig-post-card__content">
        <Image
            ratio={imageRatio}
            alt={post.caption || ""}
            src={highQualityEnabled
                ? post.media[0].imageMedium
                : post.media[0].imageSmall}
            imgClassName="gw-ig-post-card__image"
            objectFit={imageObjectFit}
            on:imageLoaded
        >
            <Icon slot="backdrop" class="gw-ig-post-card__image__icon">
                <InstagramIcon />
            </Icon>
        </Image>
        {#if post.media.length > 1}
            <span class="gw-ig-post-card__icon-wrapper">
                <Icon
                    title="It is a series of photos"
                    class="gw-ig-post-card__icon"
                >
                    <InstagramPostSeriesIcon />
                </Icon>
            </span>
        {:else if post.media[0].type === INSTAGRAM_POST_MEDIA_VIDEO_TYPE}
            <span class="gw-ig-post-card__icon-wrapper">
                <Icon title="It is a video post" class="gw-ig-post-card__icon">
                    <InstagramPostVideoIcon />
                </Icon>
            </span>
        {/if}
        <div class="gw-ig-post-card__backdrop">
            {#if post.tags.length !== 0}
                {#if displayMode === DISPLAY_MODE_SHOP_IT_TYPE}
                    <BaseButton class="gw-ig-post-card__shop-it">
                        <T
                            namespace="instagram.critical.InstagramGalleryWidget"
                            translationKey="shop_it"
                        />
                    </BaseButton>
                {:else if displayMode === DISPLAY_MODE_HOTSPOT_TYPE}
                    {#each post.tags as tag}
                        <div
                            style={`
                                left: ${tag.coordinateX * 100}%;
                                top: ${tag.coordinateY * 100}%;
                                transform:
                                    translate(
                                        -${tag.coordinateX * 100}%,
                                        -${tag.coordinateY * 100}%
                                    )`}
                            class="gw-ig-post-card__tag-wrapp"
                            data-tag-id={tag.id}
                            data-tagged-id={tag.id}
                        >
                            <TooltipActivator position="top" theme="light">
                                <span class="gw-ig-post-card__tag">
                                    <EmptyElement />
                                </span>
                                <div slot="content">
                                    {#if tag.type === INSTAGRAM_POST_TAG_PRODUCT_TYPE}
                                        <Observable
                                            observable={productsQuery.selectProduct(
                                                tag.product.id
                                            )}
                                            let:value
                                        >
                                            {#if value}
                                                <p
                                                    class="gw-ig-post-modal-viewer__tooltip-title gw-default-reset"
                                                >
                                                    {value.title}
                                                </p>
                                                <span
                                                    class="gw-ig-post-modal-viewer__tooltip-price"
                                                >
                                                    <Money
                                                        amount={value
                                                            .variants[0].price
                                                            .amount}
                                                        with_currency
                                                    />
                                                </span>
                                            {/if}
                                        </Observable>
                                    {:else}
                                        <p
                                            class="gw-ig-post-modal-viewer__tooltip-title gw-default-reset"
                                        >
                                            {tag.link.title}
                                        </p>
                                    {/if}
                                </div>
                            </TooltipActivator>
                        </div>
                    {/each}
                {/if}
            {:else}
                <Icon
                    title="Instagram icon"
                    width="30px"
                    height="30px"
                    class="gw-ig-post-card__ig-icon"
                >
                    <InstagramIcon />
                </Icon>
            {/if}
        </div>
    </div>
</div>

<style>
    .gw-ig-post-card {
        position: relative;
        width: 100%;
        height: 100%;
        cursor: pointer;
    }

    .gw-ig-post-card--square {
        height: unset;
    }

    .gw-ig-post-card--square::after {
        content: "";
        display: block;
        padding-bottom: 100%;
    }

    .gw-ig-post-card--square .gw-ig-post-card__content {
        position: absolute;
        width: 100%;
        height: 100%;
    }

    .gw-ig-post-card__icon-wrapper {
        position: absolute;
        z-index: 2;
        top: 0.5em;
        right: 0.5em;
        color: var(--gw-white-text-color);
    }

    .gw-ig-post-card__backdrop {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        transition: opacity var(--gw-default-transition);
        opacity: 0;
        background: var(--gw-light-dark-color);
    }

    .gw-ig-post-card:hover .gw-ig-post-card__backdrop {
        opacity: 1;
    }

    .gw-ig-post-card
        .gw-ig-post-card__backdrop
        :global(.gw-ig-post-card__ig-icon) {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 25px;
        height: 25px;
        transform: translateX(-50%) translateY(-50%);
        color: #fff;
    }

    .gw-ig-post-card :global(.gw-ig-post-card__image) {
        transform: scale(1);
        transition: transform 0.3s ease;
    }

    .gw-ig-post-card:hover :global(.gw-ig-post-card__image) {
        transform: scale(1.1);
    }

    .gw-ig-post-card__content :global(.gw-ig-post-card__image__icon) {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 100px;
        height: 100px;
        transform: translateX(-50%) translateY(-50%);
        color: #ccc;
    }

    .gw-ig-post-card__tag-wrapp {
        display: flex;
        position: absolute;
        z-index: 2;
        align-items: center;
        justify-content: center;
        width: 19px;
        height: 19px;
        border-radius: 50%;
        background: rgba(23, 23, 23, 0.3);
    }

    .gw-ig-post-card__tag-wrapp:hover {
        z-index: 3;
    }

    .gw-ig-post-card__tag {
        position: relative;
        width: 13px;
        height: 13px;
        transition: opacity var(--gw-default-transition);
        border-radius: 50%;
        background: var(--gw-white-bg);
        box-shadow: 0 0 0 var(--gw-ig-modal-tag-shadow-main-color);
        cursor: pointer;
    }

    .gw-ig-post-card__tag:hover + :global(.gw-tooltip) {
        display: block;
    }

    .gw-ig-post-card__backdrop :global(.gw-ig-post-card__shop-it) {
        position: absolute;
        top: 50%;
        left: 50%;
        padding: 6px 9px;
        transform: translate(-50%, -50%);
        border: 2px solid;
        color: var(--gw-white-text-color);
        font-size: 0.9em;
        font-weight: bold;
        line-height: normal;
    }

    .gw-ig-post-modal-viewer__tooltip-title {
        line-height: 1.2em;
    }
    .gw-ig-post-modal-viewer__tooltip-price {
        font-weight: bold;
    }
</style>
